import React, { useEffect, useState } from "react";
import { api, coverUrl, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Archive, ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import { CategorySection } from "../../components/admin/CategorySection";
import { buildCategoryTree } from "../../lib/categoryTree";

const STATUS_VARIANT = { live: "default", draft: "secondary", archived: "outline" };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product object
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  const [openGroups, setOpenGroups] = useState(() => JSON.parse(localStorage.getItem("admin_prod_groups") || "{}"));
  const saveGroups = (next) => { setOpenGroups(next); localStorage.setItem("admin_prod_groups", JSON.stringify(next)); };
  const toggleGroup = (id) => saveGroups({ ...openGroups, [id]: openGroups[id] === false });
  const catTree = React.useMemo(() => buildCategoryTree(cats), [cats]);
  const setAllGroups = (isOpen) => saveGroups(Object.fromEntries([...cats.map((c) => c.id), "__uncat__"].map((id) => [id, isOpen])));

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  // ---- category helpers ----
  const catById = React.useMemo(() => {
    const m = {};
    for (const c of cats) m[c.id] = c;
    return m;
  }, [cats]);

  const crumbsOf = (cid) => {
    const path = [];
    let cur = cid;
    const seen = {};
    while (cur && !seen[cur]) {
      seen[cur] = 1;
      const n = catById[cur];
      if (!n) break;
      path.unshift(n.name);
      cur = n.parent_id;
    }
    return path.join(" › ");
  };

  const flatCatList = React.useMemo(() => {
    // DFS flat list with levels for the dropdown
    const out = [];
    const childrenMap = {};
    for (const c of cats) {
      const k = c.parent_id || "__root__";
      if (!childrenMap[k]) childrenMap[k] = [];
      childrenMap[k].push(c);
    }
    Object.values(childrenMap).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    const stack = [...(childrenMap["__root__"] || [])].reverse().map((n) => ({ node: n, level: 0 }));
    while (stack.length) {
      const cur = stack.pop();
      out.push(cur);
      const kids = childrenMap[cur.node.id] || [];
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], level: cur.level + 1 });
    }
    return out;
  }, [cats]);

  const descendantsOf = (cid) => {
    const out = [cid];
    let frontier = [cid];
    while (frontier.length) {
      const next = cats.filter((c) => frontier.includes(c.parent_id || "")).map((c) => c.id);
      if (!next.length) break;
      out.push(...next);
      frontier = next;
    }
    return out;
  };

  // ---- filter items by category (includes descendants) ----
  const filteredItems = React.useMemo(() => {
    if (filterCat === "all") return items;
    if (filterCat === "none") return items.filter((p) => !(p.category_ids && p.category_ids.length));
    const wanted = new Set(descendantsOf(filterCat));
    return items.filter((p) => (p.category_ids || []).some((cid) => wanted.has(cid)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat, items, cats]);

  const archive = async (pid) => {
    if (!window.confirm("Archive this product? It will be hidden from the catalogue, but existing orders + library access are preserved.")) return;
    try {
      await api.post(`/admin/products/${pid}/archive`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("PERMANENTLY DELETE this product? This cannot be undone. If the product has orders, you'll be asked to confirm again.")) return;
    try {
      await api.delete(`/admin/products/${pid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      if (e?.response?.status === 409) {
        if (!window.confirm(detail + "\n\nForce-delete and orphan the order records?")) return;
        try {
          await api.delete(`/admin/products/${pid}?force=true`);
          toast.success("Force-deleted");
          load();
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Force delete failed");
        }
      } else {
        toast.error(detail);
      }
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground italic mt-1">
            {filteredItems.length} of {items.length} title{items.length === 1 ? "" : "s"}
            {filterCat !== "all" && filterCat !== "none" && catById[filterCat] && (
              <> · filtered by <span className="text-accent">{crumbsOf(filterCat)}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {filterCat === "all" && (
            <>
              <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => setAllGroups(true)} data-testid="prod-expand-all">
                <ChevronsUpDown className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />Expand all
              </Button>
              <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => setAllGroups(false)} data-testid="prod-collapse-all">
                <ChevronsDownUp className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />Collapse all
              </Button>
            </>
          )}
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="rounded-sm w-64" data-testid="prod-filter-cat">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="none">— Uncategorised —</SelectItem>
              {flatCatList.map(({ node, level }) => (
                <SelectItem key={node.id} value={node.id}>
                  {"\u00A0".repeat(level * 3)}
                  {level > 0 ? "↳ " : ""}
                  {node.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setEditing("new")} className="rounded-sm" data-testid="new-product">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New product
          </Button>
        </div>
      </div>

      {/* Group products by FIRST category (top-level ancestor) when filter is 'all', otherwise show as a flat grid */}
      {(() => {
        const useGrouping = filterCat === "all";
        const renderGrid = (products) => (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
            {products.map((p) => (
              <div
                key={p.id}
                className="group flex flex-col"
                data-testid={`prod-card-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => setEditing(p)}
                  className="relative aspect-[2/3] w-full overflow-hidden rounded-sm bg-muted border border-border/60 shadow-[0_4px_16px_-8px_rgba(58,31,71,0.25)] hover:border-accent transition-colors"
                  title="Edit"
                >
                  <img
                    src={coverUrl(p)}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  {p.status === "archived" && (
                    <div className="absolute top-2 left-2 bg-background/95 text-foreground text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
                      Archived
                    </div>
                  )}
                  {p.featured && p.status !== "archived" && (
                    <div className="absolute top-2 left-2 bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
                      Featured
                    </div>
                  )}
                </button>
                <div className="mt-2 flex flex-col gap-0.5">
                  <div className="font-display text-sm leading-tight line-clamp-2">{p.title}</div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold">A${p.price.toFixed(2)}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {p.units_sold > 0 ? `${p.units_sold} sold` : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-1 -ml-1.5">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} title="Edit">
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                    {p.status !== "archived" && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => archive(p.id)} data-testid={`arch-${p.id}`} title="Archive">
                        <Archive className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => remove(p.id)} data-testid={`del-${p.id}`} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

        if (filteredItems.length === 0) {
          return (
            <div className="border border-border rounded-sm p-12 text-center text-sm text-muted-foreground italic bg-card">
              {items.length === 0 ? "No products yet. Add your first title." : "Nothing in this category."}
            </div>
          );
        }

        if (!useGrouping) return renderGrid(filteredItems);

        const byCat = {};
        for (const p of filteredItems) {
          const first = (p.category_ids || []).find((cid) => catById[cid]);
          const key = first || "__uncat__";
          (byCat[key] = byCat[key] || []).push(p);
        }
        return (
          <div className="space-y-6">
            {catTree.map((n) => (
              <CategorySection key={n.id} node={n} level={0} byCat={byCat} open={openGroups} toggle={toggleGroup} renderGrid={renderGrid} />
            ))}
            {byCat.__uncat__?.length > 0 && (
              <CategorySection node={{ id: "__uncat__", name: "Uncategorised", children: [] }} level={0} byCat={byCat} open={openGroups} toggle={toggleGroup} renderGrid={renderGrid} />
            )}
          </div>
        );
      })()}

      {editing && (
        <ProductDialog
          product={editing === "new" ? null : editing}
          cats={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductDialog({ product, cats, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    title: product?.title || "",
    subtitle: product?.subtitle || "",
    description: product?.description || "",
    language: product?.language || "English",
    keywords: (product?.keywords || []).join(", "),
    price: product?.price || 9.99,
    isbn: product?.isbn || "",
    category_id: (product?.category_ids || [])[0] || "",
    status: product?.status || "live",
    featured: product?.featured || false,
  });
  const [pdf, setPdf] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (isNew) {
        if (!pdf) { toast.error("PDF file is required"); setBusy(false); return; }
        if (!pdf.name.toLowerCase().endsWith(".pdf")) {
          toast.error("File must be a .pdf");
          setBusy(false); return;
        }
        if (pdf.size > 60 * 1024 * 1024) {
          toast.error(`PDF is ${(pdf.size / 1024 / 1024).toFixed(1)}MB — max 60MB`);
          setBusy(false); return;
        }
        if (cover && cover.size > 10 * 1024 * 1024) {
          toast.error(`Cover image is ${(cover.size / 1024 / 1024).toFixed(1)}MB — max 10MB`);
          setBusy(false); return;
        }
        toast.message("Uploading & processing PDF…", { description: "This can take 30–60s for large files. Please don't close the tab." });
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("subtitle", form.subtitle);
        fd.append("description", form.description);
        fd.append("language", form.language);
        fd.append("category_ids", JSON.stringify(form.category_id ? [form.category_id] : []));
        fd.append("keywords", JSON.stringify(form.keywords.split(",").map((k) => k.trim()).filter(Boolean)));
        fd.append("price", form.price);
        fd.append("isbn", form.isbn);
        fd.append("status", form.status);
        fd.append("featured", String(form.featured));
        fd.append("pdf", pdf);
        if (cover) fd.append("cover", cover);
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/admin/products`, { method: "POST", body: fd, credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) {
          let detail = `Upload failed (HTTP ${res.status})`;
          try {
            const j = await res.json();
            if (j?.detail) detail = j.detail;
          } catch {
            try { const t = await res.text(); if (t) detail = t.slice(0, 200); } catch { /* ignore */ }
          }
          throw new Error(detail);
        }
        toast.success("Product created");
      } else {
        // EDIT — first save text fields via PATCH, then upload new files if any
        const body = {
          title: form.title, subtitle: form.subtitle, description: form.description,
          language: form.language,
          keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
          price: parseFloat(form.price), isbn: form.isbn, status: form.status, featured: form.featured,
          category_ids: form.category_id ? [form.category_id] : [],
        };
        await api.patch(`/admin/products/${product.id}`, body);

        if (pdf || cover) {
          if (pdf && !pdf.name.toLowerCase().endsWith(".pdf")) {
            toast.error("File must be a .pdf"); setBusy(false); return;
          }
          if (pdf && pdf.size > 60 * 1024 * 1024) {
            toast.error(`PDF is ${(pdf.size / 1024 / 1024).toFixed(1)}MB — max 60MB`);
            setBusy(false); return;
          }
          if (cover && cover.size > 10 * 1024 * 1024) {
            toast.error(`Cover image is ${(cover.size / 1024 / 1024).toFixed(1)}MB — max 10MB`);
            setBusy(false); return;
          }
          toast.message("Uploading new file(s)…", { description: "This can take 30–60s for large PDFs." });
          const fd2 = new FormData();
          if (pdf) fd2.append("pdf", pdf);
          if (cover) fd2.append("cover", cover);
          const token = localStorage.getItem("token");
          const res = await fetch(`${API}/admin/products/${product.id}/files`, {
            method: "POST", body: fd2, credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) {
            let detail = `Upload failed (HTTP ${res.status})`;
            try { const j = await res.json(); if (j?.detail) detail = j.detail; }
            catch { try { const t = await res.text(); if (t) detail = t.slice(0, 200); } catch { /* ignore */ } }
            throw new Error(detail);
          }
          toast.success(pdf && cover ? "PDF & cover replaced" : pdf ? "PDF replaced (cover & previews regenerated)" : "Cover replaced");
        } else {
          toast.success("Product updated");
        }
      }
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "New product" : "Edit product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isNew ? (
            <>
              <div>
                <Label>PDF file *</Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0])} className="rounded-sm mt-1.5" data-testid="dlg-pdf" />
              </div>
              <div>
                <Label>Cover image (optional — auto from page 1)</Label>
                <Input type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0])} className="rounded-sm mt-1.5" data-testid="dlg-cover" />
              </div>
            </>
          ) : (
            <div className="border border-dashed border-border rounded-sm p-4 space-y-3 bg-muted/30">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Replace files (optional)</div>
              <div>
                <Label>New PDF</Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0])} className="rounded-sm mt-1.5 bg-background" data-testid="dlg-pdf-replace" />
                <p className="text-[11px] text-muted-foreground mt-1 italic">
                  Leave blank to keep the current PDF. Replacing it also regenerates the 3-page sample preview, and the cover (unless you upload one below). Existing buyers re-downloading will get the new version.
                </p>
              </div>
              <div>
                <Label>New cover image</Label>
                <Input type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0])} className="rounded-sm mt-1.5 bg-background" data-testid="dlg-cover-replace" />
                <p className="text-[11px] text-muted-foreground mt-1 italic">
                  Leave blank to keep the current cover. JPG / PNG / WebP, up to 10MB.
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="dlg-title" /></div>
            <div><Label>Price (AUD) *</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm mt-1.5" data-testid="dlg-price" /></div>
          </div>
          <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="rounded-sm mt-1.5" data-testid="dlg-subtitle" /></div>
          <div><Label>Description *</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1.5" data-testid="dlg-description" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="dlg-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Spanish">Spanish</SelectItem>
                  <SelectItem value="French">French</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="dlg-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.parent_id ? "— " : ""}{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="dlg-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Keywords (comma separated)</Label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="poetry, romance, literary" className="rounded-sm mt-1.5" data-testid="dlg-keywords" /></div>
          <div><Label>ISBN (optional)</Label><Input value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} className="rounded-sm mt-1.5" /></div>
          <div className="flex items-center gap-2">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} id="featured" data-testid="dlg-featured" />
            <Label htmlFor="featured">Feature on home page</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm" data-testid="dlg-save">{busy ? "Saving…" : isNew ? "Create" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
